/**
 * Live Chat Service Types
 *
 * Type definitions for the live chat feature.
 * Components and services depend on these abstractions.
 */

import type { Signal } from "@lit-labs/signals";
import type { ReadonlySignal } from "../../di/signal-types";

export const SYSTEM_USER_ID = "00000000-0000-0000-0000-000000000000";

export type SystemMessageContent = "EVENT_STARTED" | "EVENT_ENDED";

export function isSystemMessage(message: IChatMessage): boolean {
  return message.userId === SYSTEM_USER_ID;
}

/**
 * Attachment on a chat message.
 * Shape matches the resolved attachment schema in api-contracts/rpc/live-chat.ts
 * and api-contracts/rpc/community.ts.
 */
export interface IChatAttachment {
  fileId: string;
  url: string;
  mimeType: string;
  originalName: string;
  sizeBytes: number;
}

/**
 * Single chat message structure.
 * Matches the shape returned by get-live-chat-messages RPC
 * and dispatched via live-chat-message SSE event.
 */
export interface IChatMessage {
  id: string;
  userId: string;
  userName: string;
  userAvatarUrl: string | null;
  content: string;
  sentAt: string;
  /**
   * File attachments on this message. Owner-only on send.
   * Optional in the type to keep historical fixtures and tests valid;
   * the backend always returns an array (possibly empty).
   */
  attachments?: IChatAttachment[];
  authorIsProtected: boolean;
}

/**
 * Lightweight descriptor of an already-uploaded attachment being sent with a message.
 */
export interface INewChatAttachment {
  fileId: string;
  originalName: string;
  sizeBytes: number;
}

/**
 * Chat connection status.
 * - "idle": No active chat session
 * - "loading": Fetching initial messages
 * - "connected": Chat is active and receiving messages
 * - "error": Failed to load or connection issue
 */
export type ChatConnectionStatus = "idle" | "loading" | "connected" | "error";

/**
 * Read-only interface for live chat state.
 * UI components should use this - no .set() access.
 */
export interface ILiveChatState {
  readonly messages: ReadonlySignal<IChatMessage[]>;
  readonly connectionStatus: ReadonlySignal<ChatConnectionStatus>;
  readonly currentEventId: ReadonlySignal<string | null>;
  readonly errorMessage: ReadonlySignal<string>;
  readonly isSending: ReadonlySignal<boolean>;
  readonly unreadCount: ReadonlySignal<number>;
  readonly isViewing: ReadonlySignal<boolean>;
  readonly hasMore: ReadonlySignal<boolean>;
  readonly loadingOlder: ReadonlySignal<boolean>;
  readonly isBanned: ReadonlySignal<boolean>;
  readonly currentCreatorId: ReadonlySignal<string | null>;
}

/**
 * Writable interface for live chat state.
 * Only LiveChatService should use this.
 */
export interface IWritableLiveChatState {
  readonly messages: Signal.State<IChatMessage[]>;
  readonly connectionStatus: Signal.State<ChatConnectionStatus>;
  readonly currentEventId: Signal.State<string | null>;
  readonly errorMessage: Signal.State<string>;
  readonly isSending: Signal.State<boolean>;
  readonly unreadCount: Signal.State<number>;
  readonly isViewing: Signal.State<boolean>;
  readonly hasMore: Signal.State<boolean>;
  readonly loadingOlder: Signal.State<boolean>;
  readonly isBanned: Signal.State<boolean>;
  readonly currentCreatorId: Signal.State<string | null>;
}

/**
 * Interface for live chat service.
 * Manages chat lifecycle, message sending, and SSE event handling.
 */
export interface ILiveChatService {
  /**
   * Initialize chat for a specific live event or community.
   * Loads initial messages and starts listening for SSE events.
   * @param chatId The event id (for live chat) or community id (for community chat) to connect to
   * @param creatorInfluencerId The creator (influencer) who owns the chat — required for creator-scoped ban filtering
   */
  connect(chatId: string, creatorInfluencerId: string): Promise<void>;

  /**
   * Load older messages before the oldest currently loaded message.
   * Used for infinite scroll pagination.
   */
  loadOlderMessages(): Promise<void>;

  /**
   * Disconnect from current chat session.
   * Clears messages and stops SSE listener.
   */
  disconnect(): void;

  /**
   * Send a message to the current chat.
   * @param content Message text (1-1000 characters)
   * @param notifyByEmail When true, also sends email notification to ticket holders (creator only)
   * @returns Promise resolving to success status
   */
  sendMessage(content: string, notifyByEmail?: boolean, attachments?: INewChatAttachment[]): Promise<boolean>;

  /**
   * Mark all current messages as read, resetting the unread count.
   */
  markAsRead(): void;

  /**
   * Set whether the user is currently viewing the chat.
   * When viewing, new messages won't increment the unread count.
   */
  setViewing(viewing: boolean): void;

  /**
   * Dispose the service and clean up resources.
   */
  dispose(): void;
}
